import { useEffect, useRef } from 'react';

export type AgentKeyboardHandlers = {
  onClose: () => void;
  onFocusSearch: () => void;
  onFocusNext: () => void;
  onFocusPrev: () => void;
  onCommitFocused: () => void;
  onPushToTalkStart?: () => void;
  onPushToTalkEnd?: () => void;
};

export type UseAgentKeyboardOptions = AgentKeyboardHandlers & {
  enabled: boolean;
};

const EDITABLE_TAG_NAMES = new Set<string>(['INPUT', 'TEXTAREA', 'SELECT']);

const KEY = {
  Escape: 'Escape',
  Slash: '/',
  ArrowDown: 'ArrowDown',
  ArrowUp: 'ArrowUp',
  Enter: 'Enter',
  Alt: 'Alt',
} as const;

const isEditableTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return EDITABLE_TAG_NAMES.has(target.tagName);
};

export function useAgentKeyboard({
  enabled,
  onClose,
  onFocusSearch,
  onFocusNext,
  onFocusPrev,
  onCommitFocused,
  onPushToTalkStart,
  onPushToTalkEnd,
}: UseAgentKeyboardOptions): void {
  const altHeldRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === KEY.Escape) {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key === KEY.Slash && !isEditableTarget(event.target)) {
        event.preventDefault();
        onFocusSearch();
        return;
      }

      if (
        event.key === KEY.Alt &&
        !altHeldRef.current &&
        !isEditableTarget(event.target)
      ) {
        altHeldRef.current = true;
        onPushToTalkStart?.();
        return;
      }

      if (event.key === KEY.ArrowDown) {
        event.preventDefault();
        onFocusNext();
        return;
      }

      if (event.key === KEY.ArrowUp) {
        event.preventDefault();
        onFocusPrev();
        return;
      }

      if (event.key === KEY.Enter && !isEditableTarget(event.target)) {
        event.preventDefault();
        onCommitFocused();
        return;
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === KEY.Alt && altHeldRef.current) {
        altHeldRef.current = false;
        onPushToTalkEnd?.();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
    };
  }, [
    enabled,
    onClose,
    onFocusSearch,
    onFocusNext,
    onFocusPrev,
    onCommitFocused,
    onPushToTalkStart,
    onPushToTalkEnd,
  ]);
}
